import { VULNERABILITY_CATEGORIES, VulnerabilityCategory } from "./api/endpoints/reports/reports";
import vrtList from "./api/endpoints/reports/vrt_list.json";
import owaspCweTaxonomy from "./api/endpoints/reports/owasp_cwe_taxonomy.json";

export interface CategoryPath {
  categoryId: string;
  subcategoryId?: string;
  variantId?: string;
}

const LEGACY_ID_MAP: Record<string, string> = {
  // Categories
  cryptographic_weakness: "a04_2025_cryptographic_failures",
  broken_authentication_and_session_management: "a07_2025_authentication_failures",
  insufficient_logging_and_monitoring: "a09_2025_security_logging_and_alerting_failures",
  api_security: "owasp_api_security_top_10_2023",
  
  // Subcategories
  idor: "a01_2025_broken_access_control",
  privilege_escalation: "vertical_privilege_escalation",
  
  // Variants
  idor_read_only: "insecure_direct_object_reference_idor_read_only",
  idor_read_write: "insecure_direct_object_reference_idor_read_write"
};

// Recursively find a node by ID and return the path of nodes from the root
const findPathById = (
  nodes: VulnerabilityCategory[],
  id: string,
  currentPath: VulnerabilityCategory[] = []
): VulnerabilityCategory[] | null => {
  for (const node of nodes) {
    const path = [...currentPath, node];
    if (node.id === id) {
      return path;
    }
    if (node.children) {
      const result = findPathById(node.children, id, path);
      if (result) return result;
    }
  }
  return null;
};

// Find a node path using legacy mappings or normalized fuzzy matching
export const findPathByLegacyId = (
  nodes: VulnerabilityCategory[],
  legacyId: string
): VulnerabilityCategory[] | null => {
  const mappedId = LEGACY_ID_MAP[legacyId] || legacyId;
  let path = findPathById(nodes, mappedId);
  if (path) return path;

  const normLegacy = legacyId.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!normLegacy) return null;

  const searchSubstring = (
    currentNodes: VulnerabilityCategory[],
    currentPath: VulnerabilityCategory[] = []
  ): VulnerabilityCategory[] | null => {
    for (const node of currentNodes) {
      const path = [...currentPath, node];
      const normNodeId = node.id.toLowerCase().replace(/[^a-z0-9]/g, "");
      
      if (normNodeId.includes(normLegacy) || normLegacy.includes(normNodeId)) {
        return path;
      }
      
      if (node.children) {
        const result = searchSubstring(node.children, path);
        if (result) return result;
      }
    }
    return null;
  };

  return searchSubstring(nodes);
};

const datasets: VulnerabilityCategory[][] = [
  VULNERABILITY_CATEGORIES,
  vrtList as VulnerabilityCategory[],
  owaspCweTaxonomy as any as VulnerabilityCategory[]
];

export const getCategoryDisplayName = (categoryPath: CategoryPath): string => {
  // 1. Try direct path resolution across all datasets
  for (const dataset of datasets) {
    const category = dataset.find(
      c => c.id === categoryPath.categoryId
    );

    if (category) {
      let result = category.name;

      if (categoryPath.subcategoryId && category.children) {
        const subcategory = category.children.find(
          sc => sc.id === categoryPath.subcategoryId
        );
        if (subcategory) {
          result += ` > ${subcategory.name}`;

          if (categoryPath.variantId && subcategory.children) {
            const variant = subcategory.children.find(
              v => v.id === categoryPath.variantId
            );
            if (variant) {
              result += ` > ${variant.name}`;
            }
          }
        }
      }
      return result;
    }
  }

  // 2. Fallback: Search the entire tree for the deepest ID specified in the path
  const targetId = categoryPath.variantId || categoryPath.subcategoryId || categoryPath.categoryId;
  if (targetId) {
    for (const dataset of datasets) {
      const path = findPathByLegacyId(dataset, targetId);
      if (path) {
        return path.map(n => n.name).join(" > ");
      }
    }
  }

  return "";
};

export const parseCategoryString = (category: string): string => {
  try {
    const parsed: CategoryPath = JSON.parse(category);
    const displayName = getCategoryDisplayName(parsed);
    return displayName || category; // Fallback to original if display name is empty
  } catch (e) {
    return category; // Not a valid JSON string
  }
};
